import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, ElementRef } from '@angular/core';
import { Select, Store, Actions, ofActionDispatched } from '@ngxs/store';
import { EngineState } from 'src/app/engine/state/engine.state';
import { combineLatest, Observable, Subject } from 'rxjs';
import { StoryLabelsState } from 'src/app/story/state/labels/story.labels.state';
import { LabelModel } from 'src/app/story/models/labels/label.model';
import { ViewTreeNode } from 'src/app/engine/models/view.tree-node';
import { ToolEnum } from 'src/app/engine/models/tool.enum';
import { filter, map, takeUntil } from 'rxjs/operators';
import { AddLabel, SelectLabel, AddContextLabel, RemoveContextLabel, SetLabelsShown } from 'src/app/story/state/labels/story.labels.actions';
import { RemoveLabel, SetAllLabelsShown, SetLabel } from 'src/app/story/state/labels/story.labels.actions';
import { HitModel } from 'src/app/engine/models/hit.model';
import { CameraService } from 'src/app/engine/camera.service';
import { ClickNode, ToggleLabels } from 'src/app/engine/state/engine.actions';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { EngineCameraState } from 'src/app/engine/state/camera/engine.camera.state';
import { ChapterModel } from 'src/app/story/models/chapter.model';
import { SetCamera } from 'src/app/engine/state/camera/engine.camera.actions';
import { LabelsService } from 'src/app/story/labels.service';
import { LabelThemeEnum } from 'src/app/story/models/labels/label-theme.enum';
import { EngineNodesState } from 'src/app/engine/state/nodes/engine.nodes.state';
import { ErrandType } from 'src/app/story/models/errands/errand-type.enum';
import { FlyToObjectErrandModel } from 'src/app/story/models/errands/fly-to-object-errand.model';
import { FlyToChapterErrandModel } from 'src/app/story/models/errands/fly-to-chapter-errand.model';
import { cloneDeep, get, isNil } from 'lodash';
import { EngineService } from 'src/app/engine/engine.service';
import { EventsEnum } from 'src/app/engine/models/events.enum';
import { ViewState } from 'src/app/view/state/view.state';
import { ViewUIModel } from 'src/app/view/models/view-ui.model';
import { LabelCollapseModeEnum } from 'src/app/story/models/labels/label-collapse-mode';
import { QuizState } from 'src/app/quiz/state/quiz.state';
import { QuizQuestionUnion } from 'src/app/quiz/models/quiz-question.model';
import { QuizQuestionType } from 'src/app/quiz/models/quiz-question-type.enum';
import { Router } from '@angular/router';

@Component({
  selector: 'app-labels',
  templateUrl: './labels.component.html',
  styleUrls: ['./labels.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LabelsComponent implements OnInit, OnDestroy {

  @Select(ViewState.getUI)
  layout$: Observable<ViewUIModel>;

  @Select(StoryLabelsState.getChapterLabels)
  labels$: Observable<LabelModel[]>;
  
  @Select(QuizState.getQuizModeActive)
  quizModeActive$: Observable<boolean>;

  @Select(EngineState.getActiveTool)
  activeTool$: Observable<ToolEnum>;

  @Select(QuizState.getActiveQuestion)
  activeQuestion$: Observable<QuizQuestionUnion>;

  @Select(StoryChaptersState.getSelectedChapter)
  selectedChapter$: Observable<ChapterModel>;

  @Select(StoryLabelsState.getSelectedLabel)
  selectedLabel$: Observable<LabelModel>;

  @Select(StoryLabelsState.getLabelsCollapsed)
  labelsCollapsed$: Observable<boolean>;

  @Select(StoryLabelsState.getLabelsCollapsedMode)
  labelsCollapsedMode$: Observable<LabelCollapseModeEnum>;

  @Select(StoryLabelsState.getContextLabel)
  contextLabel$: Observable<LabelModel>;

  @Select(EngineState.getShowLabels)
  showLabels$: Observable<boolean>;

  @Select(EngineCameraState.getZoomed)
  zoomed$: Observable<boolean>;

  @Select(StoryLabelsState.getEnableContextLabel)
  enableContextLabel$: Observable<boolean>;

  @Select(StoryLabelsState.getEnableContextLabelInfo)
  enableContextLabelInfo$: Observable<boolean>;

  @Select(StoryLabelsState.getEnableContextLabelActions)
  enableContextLabelActions$: Observable<boolean>;

  @Select(StoryLabelsState.getLabelsDraggable)
  draggable$: Observable<{ pin: boolean; tag: boolean}>;

  interactive = false;
  // studio labels
  showAnatomyLabels = false;
  // quiz labels
  showQuizLabels = false;

  private ngUnsubscribe = new Subject();

  constructor(
    private store: Store,
    private action$: Actions,
    private router: Router,
    private engineService: EngineService,
    private cameraService: CameraService,
    private changeDetector: ChangeDetectorRef,
    private labelsService: LabelsService,
    private elRef: ElementRef
  ) {}

  ngOnInit() {
    this.bindEngineEvents();
    this.bindApplicationEvents();

    this.labelsService.setLabelContainer(this.elRef);
  }

  ngOnDestroy() {
    this.labelsService.setLabelContainer(null);

    this.changeDetector.detach();   

    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  bindApplicationEvents() {
    // Listen for node click
    this.action$
      .pipe(
        ofActionDispatched(ClickNode),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((action: ClickNode) => {
        this.handleClickNode(action);
      });

    // Change detection
    this.labels$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((labels) => {
        this.changeDetector.markForCheck();
      });
    
    // Handle label deselection
    this.selectedLabel$
      .pipe(
        filter(label => label == null),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        const zoomed = this.store.selectSnapshot(EngineCameraState.getZoomed);
        const lockCamera = this.store.selectSnapshot(EngineCameraState.getLockCamera);
        if (zoomed && !lockCamera) {
          this.resetCamera();
        }
      });

    // quiz label visibility
    const showQuizLabels$ = combineLatest([
      this.activeTool$,
      this.activeQuestion$
    ])
      .pipe(
        takeUntil(this.ngUnsubscribe),
        map(([activeTool, activeQuestion]) => {
          const isEditMode = this.router.url.indexOf('/edit') >= 0;
          const isLabelAnatomyQuestion = get(activeQuestion, 'type') === QuizQuestionType.LabelAnatomy;

          return isEditMode
            ? activeTool === ToolEnum.Quiz // edit mode
            : isLabelAnatomyQuestion; // view mode
        })
      );
    
    // anatomy vs quiz labels: mutually exclusive
    combineLatest([
      this.showLabels$,
      showQuizLabels$
    ])
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(([showAnatomyLabels, showQuizLabels]) => {
        this.showQuizLabels = showQuizLabels;
        this.showAnatomyLabels = !this.showQuizLabels && showAnatomyLabels;

        this.changeDetector.markForCheck();
      });

    // Listen for ready state before showing
    this.store.select(StoryChaptersState.getSelectedChapterReadyState).pipe(
      filter(readyState => readyState != null),
      takeUntil(this.ngUnsubscribe)
    ).subscribe(readyState => {
      this.interactive = readyState.model;
      this.changeDetector.markForCheck();
    });

  }

  bindEngineEvents() {
    /*
     polyfill engine events
      - dispatch label events via the engine for subscriptions.
      - used for handling legacy annotation and hotspot events.
    */

    // ToggleLabels
    this.action$
      .pipe(
        ofActionDispatched(ToggleLabels, SetAllLabelsShown),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(action => {
        const engineEvent = action.show ? EventsEnum. LabelsEnabled : EventsEnum.LabelsDisabled;
        this.engineService.fireEvent(engineEvent, null);
      });

    // AddLabel
    this.action$
      .pipe(
        ofActionDispatched(AddLabel),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(action => {
        this.engineService.fireEvent(EventsEnum.LabelsCreated, action.label);
      });

    // SetLabel
    this.action$
      .pipe(
        ofActionDispatched(SetLabel),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(action => {
        this.engineService.fireEvent(EventsEnum.LabelsUpdated, action.label);
      });

    // SetLabel
    this.action$
      .pipe(
        ofActionDispatched(SetLabelsShown),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(action => {
        action.labels.forEach(label => {
          const labelEvent = action.shown ? EventsEnum.LabelsShown : EventsEnum.LabelsHidden;
          this.engineService.fireEvent(labelEvent, {'labelId': label.id});
        });
      });

      // RemoveLabel
    this.action$
      .pipe(
        ofActionDispatched(RemoveLabel),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(action => {
        this.engineService.fireEvent(EventsEnum.LabelsDestroyed, action.label);
      });

  }

  handleClickNode(action: ClickNode) {
    const tool = this.store.selectSnapshot(EngineState.getActiveTool);

    if (
      action.node != null
    ) {
      if (tool === ToolEnum.Label) {
        this.addLabel(action.hit, action.node);
      } else {
        // Timeout to allow selected node to update
        setTimeout(() => {
          this.toggleContextLabel(action.hit, action.node);
        });
      }
    } else {
      this.store.dispatch(new SelectLabel(null));
      this.store.dispatch(new RemoveContextLabel());
    }
  }

  addLabel(hit: HitModel, node: ViewTreeNode) {
    const direction = this.cameraService.getDirection(hit);
    const label = this.getLabelDefaults(hit.objectId);
    
    const tagDistance = label.hideWire ? 20 : 32;

    label.title = node.label;
    label.offset = [tagDistance, 0];
    label.objectId = hit.objectId;
    label.barycentric = [hit.barycentric[0], hit.barycentric[1], hit.barycentric[2]];
    label.pos = [hit.worldPos[0], hit.worldPos[1], hit.worldPos[2]];
    label.primitiveIndex = hit.primitiveIndex;
    label.renderableIndex = hit.renderableIndex || 0;
    label.dir = direction;
    label.chapterId = this.store.selectSnapshot(StoryChaptersState.getSelectedChapter).id;
    label.occludable = true;
    label.shown = true;

    this.store.dispatch(new AddLabel(label));
    this.store.dispatch(new SelectLabel(label));
  }

  toggleContextLabel(hit: HitModel, node: ViewTreeNode) {
    const nodeIds = this.store.selectSnapshot(EngineNodesState.getCheckedNodeIds);

    if (nodeIds.some(value => value === node.id)) {
      const direction = this.cameraService.getDirection(hit);
      const label = this.labelsService.makeDefaultStudioLabel();
      
      label.theme = LabelThemeEnum.Context;
      label.title = node.label;
      label.offset = [32, 0];
      label.objectId = hit.objectId;
      label.barycentric = [hit.barycentric[0], hit.barycentric[1], hit.barycentric[2]];
      label.pos = [hit.worldPos[0], hit.worldPos[1], hit.worldPos[2]];
      label.primitiveIndex = hit.primitiveIndex;
      label.dir = direction;
  
      label.occludable = false;
  
      this.store.dispatch(new AddContextLabel(label));
    } else {
      this.store.dispatch(new RemoveContextLabel());
    }
  }

  getLabelDefaults(objectId: string) {
    // Label copy order of priority
    // 1. Selected
    // 2. Last in chapter
    // 3. Last in story
    // 4. New Studio Label

    let labelToCopy = this.store.selectSnapshot(StoryLabelsState.getSelectedLabel);

    if (labelToCopy == null) {
      // Get chapter labels
      let labels = this.store.selectSnapshot(StoryLabelsState.getChapterLabels);

      // Get story labels
      if (!labels || labels.length === 0) {
        labels = this.store.selectSnapshot(StoryLabelsState.getLabels);
      }

      // If labels, assign
      if (labels.length) {
        labelToCopy = cloneDeep(labels[labels.length - 1]);
      }
    }

    if (labelToCopy != null) {
      // Make a shallow copy of the most recent label
      const label = this.labelsService.makeDefaultStudioLabel();
      label.hideWire = labelToCopy.hideWire;
      label.pinGlow = labelToCopy.pinGlow;
      label.pinType = labelToCopy.pinType;
      label.theme = labelToCopy.theme;
      label.sequentialType = labelToCopy.sequentialType;

      // Emulate previous labels actions, but overwrite targets
      
      // -- Default State
      label.states[0].errands[0] = new FlyToChapterErrandModel();
      
      // -- Selected State
      switch (get(labelToCopy, 'states[1].errands[0].type')) {
        case ErrandType.flyToObject:
        case ErrandType.flyToCamera:
          label.states[1].errands[0] = new FlyToObjectErrandModel(objectId);
          break;
        case ErrandType.flyToChapter:
          // noop
      }

      return label;
    } else {
      // New label
      const label = this.labelsService.makeDefaultStudioLabel();
      label.states[0].errands[0] = new FlyToChapterErrandModel();
      label.states[1].errands[0] = new FlyToObjectErrandModel(objectId);  
      return label;
    }
  }

  trackLabel(index: number, label: LabelModel) {
    const { id, shown, theme } = label;
    return `${id}${shown}${theme}`;
  }

  resetCamera() {
    const chapter = this.store.selectSnapshot(StoryChaptersState.getSelectedChapter);
    const location = get(chapter, 'camera.location');
    if (location) {
      this.store.dispatch(new SetCamera(chapter.camera.location));
    }
  }
}
